import { getColorIslands } from './islandTraversal.js';

// Existing game logic functions
export const calculateScore = (gameBoard, playerId) => {
  const islands = getColorIslands(gameBoard, playerId);
  return islands.reduce((score, island) => {
    if (island.size === 1) return score;
    if (island.size === 2) return score + 1;
    if (island.size === 3) return score + 4;
    return score + 7 + (island.size - 4) * 2;
  }, 0);
};

export const placeMarker = (gameBoard, boardY, boardX, playerId) => {
  const newBoard = gameBoard.map(row => [...row]);
  newBoard[boardY][boardX] = playerId;
  
  const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]];
  for (const [dy, dx] of directions) {
    const ny = boardY + dy;
    const nx = boardX + dx;
    if (ny >= 0 && ny < newBoard.length && nx >= 0 && nx < newBoard[0].length) {
      if (newBoard[ny][nx] === 'X') {
        newBoard[ny][nx] = 'Y';
      }
    }
  }
  
  return newBoard;
};

/**
 * Generate map using random walk (original algorithm)
 * Creates linear, corridor-like maps
 * Good for: Blocking strategies, tactical choke points
 */
const generateMapRandomWalk = (numPlayers, numCols, numRows, mapSize) => {
  const gameBoard = Array(numRows).fill(null).map(() => Array(numCols).fill('B'));
  const desiredPlayableArea = mapSize * numPlayers;
  
  const isValid = (row, col) => row >= 0 && row < numRows && col >= 0 && col < numCols;
  
  let currRow = Math.floor(numRows / 2);
  let currCol = Math.floor(numCols / 2);
  gameBoard[currRow][currCol] = 'Y';
  let playableArea = 1;
  let neverendingLoop = 0;
  
  while (playableArea < desiredPlayableArea) {
    if (neverendingLoop > 50) {
      currRow = Math.floor(numRows / 2);
      currCol = Math.floor(numCols / 2);
      neverendingLoop = 0;
    }
    
    const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]];
    const direction = directions[Math.floor(Math.random() * directions.length)];
    const nextRow = currRow + direction[0];
    const nextCol = currCol + direction[1];
    
    if (isValid(nextRow, nextCol) && gameBoard[nextRow][nextCol] === 'B') {
      currRow = nextRow;
      currCol = nextCol;
      gameBoard[currRow][currCol] = 'X';
      playableArea += 1;
    } else {
      if (isValid(nextRow, nextCol)) {
        currRow = nextRow;
        currCol = nextCol;
      }
      neverendingLoop += 1;
    }
  }
  
  return gameBoard;
};

/**
 * Generate map using frontier expansion
 * Creates compact, blob-like maps
 * Good for: Building large islands, less blocking
 */
const generateMapFrontier = (numPlayers, numCols, numRows, mapSize) => {
  const gameBoard = Array(numRows).fill(null).map(() => Array(numCols).fill('B'));
  const desiredPlayableArea = mapSize * numPlayers;
  
  const centerRow = Math.floor(numRows / 2);
  const centerCol = Math.floor(numCols / 2);
  
  gameBoard[centerRow][centerCol] = 'Y';
  
  const frontier = new Set();
  const addToFrontier = (row, col) => {
    if (row >= 0 && row < numRows && col >= 0 && col < numCols && 
        gameBoard[row][col] === 'B') {
      frontier.add(`${row},${col}`);
    }
  };
  
  const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]];
  for (const [dy, dx] of directions) {
    addToFrontier(centerRow + dy, centerCol + dx);
  }
  
  let playableArea = 1;
  
  while (playableArea < desiredPlayableArea && frontier.size > 0) {
    const frontierArray = Array.from(frontier);
    const randomIdx = Math.floor(Math.random() * frontierArray.length);
    const [row, col] = frontierArray[randomIdx].split(',').map(Number);
    
    frontier.delete(frontierArray[randomIdx]);
    
    gameBoard[row][col] = 'X';
    playableArea++;
    
    for (const [dy, dx] of directions) {
      addToFrontier(row + dy, col + dx);
    }
  }
  
  return gameBoard;
};

/**
 * Generate map using drunkard walk with backtracking
 * Creates sprawling, asymmetric maps with tendrils
 * Good for: Strategic positioning, varied gameplay
 */
const generateMapDrunkard = (numPlayers, numCols, numRows, mapSize) => {
  const gameBoard = Array(numRows).fill(null).map(() => Array(numCols).fill('B'));
  const desiredPlayableArea = mapSize * numPlayers;
  
  const centerRow = Math.floor(numRows / 2);
  const centerCol = Math.floor(numCols / 2);
  
  gameBoard[centerRow][centerCol] = 'Y';
  
  const visited = new Set([`${centerRow},${centerCol}`]);
  let currRow = centerRow;
  let currCol = centerCol;
  
  const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]];
  
  while (visited.size < desiredPlayableArea) {
    // Capture current position to avoid closure issues in loop
    const row = currRow;
    const col = currCol;
    
    const validMoves = directions
      .map(([dy, dx]) => [row + dy, col + dx])
      .filter(([r, c]) => 
        r >= 0 && r < numRows && 
        c >= 0 && c < numCols && 
        !visited.has(`${r},${c}`)
      );
    
    if (validMoves.length > 0) {
      const [newRow, newCol] = validMoves[Math.floor(Math.random() * validMoves.length)];
      currRow = newRow;
      currCol = newCol;
      gameBoard[currRow][currCol] = 'X';
      visited.add(`${currRow},${currCol}`);
    } else {
      const visitedArray = Array.from(visited)
        .map(key => key.split(',').map(Number))
        .filter(([r, c]) => 
          directions.some(([dy, dx]) => {
            const nr = r + dy, nc = c + dx;
            return nr >= 0 && nr < numRows && nc >= 0 && nc < numCols && 
                   !visited.has(`${nr},${nc}`);
          })
        );
      
      if (visitedArray.length === 0) break;
      
      const [backtrackRow, backtrackCol] = visitedArray[
        Math.floor(Math.random() * visitedArray.length)
      ];
      currRow = backtrackRow;
      currCol = backtrackCol;
    }
  }
  
  return gameBoard;
};

/**
 * Main map generation function
 * @param {number} numPlayers - Number of players
 * @param {number} numCols - Map width
 * @param {number} numRows - Map height
 * @param {number} mapSize - Tiles per player
 * @param {string} algorithm - 'corridors' | 'compact' | 'sprawling'
 */
export const generateMap = (numPlayers, numCols, numRows, mapSize, algorithm = 'corridors') => {
  switch (algorithm) {
    case 'compact':
      return generateMapFrontier(numPlayers, numCols, numRows, mapSize);
    case 'sprawling':
      return generateMapDrunkard(numPlayers, numCols, numRows, mapSize);
    case 'corridors':
    default:
      return generateMapRandomWalk(numPlayers, numCols, numRows, mapSize);
  }
};

/**
 * Find the next active player (skipping players with no markers).
 * Returns null if no active player remains.
 *
 * @param {number} currentIndex - current player index
 * @param {Array} allPlayers - list of all players
 * @returns {number|null}
 */
export const getNextActivePlayer = (currentIndex, allPlayers) => {
  const count = allPlayers.length;
  if (count === 0) return null;

  let next = (currentIndex + 1) % count;
  let attempts = 0;

  while (allPlayers[next]?.markers?.length === 0 && attempts < count) {
    next = (next + 1) % count;
    attempts++;
  }

  return attempts >= count ? null : next;
};